import { t } from 'i18next';
import { notifySuccess } from '../../api/axios';
import { IShipmentLoadBoardViewItem } from '../../api/shipment/interfaces';
import { shipmentsApi } from '../../api/shipment/shipmentsApi';
import { RangeValue } from '../../components/Form/DateRangeInputField/DateRangeInputField';
import i18n from '../../i18n/i18nInit';
import { API_CONSTANTS } from '../../services/apiConstants';
import { ApplyFiltersFormField } from './components/LoadBoardFilters/SearchFilters/ApplyFiltersForm.config';
import {
    searchDateFilterDetailsMap,
    searchFilterDetailsMap,
} from './components/LoadBoardList/utils/FilterUtil';
import { ILoadBoardSearchFilters, ILoadBoardStatusFilters } from './interfaces';
import { ISelectedDriverIdList } from './interfaces/ISelectedDriverList';
import { setIsSubmitting } from '../../common/Submitting/SubmittingButtonReducer';
import { AppDispatch } from '../../redux/store';
import { dispatchManagerFeatures } from '../../featureFlags/dispatchManagerFeatures';

const {
    isEnhancedLoadBoardEnabled,
    isODataFilterFixEnabled,
    isTrailerSizePreferencesEnabled,
} = dispatchManagerFeatures;

const TRAILER_SIZE_INCLUDES = [
    'TrailerRequirements',
    'TrailerRequirements.TrailerSize',
] as const;

export const buildFetchShipmentDetailsApiUrl = (
    loadBoardSearchFilters: ILoadBoardSearchFilters,
    loadBoardBadgesFilters: ILoadBoardStatusFilters,
    isInclude: boolean,
): string => {
    const { driverName, customerName, orderDate, orderNumber, contractNumber } =
        loadBoardSearchFilters;
    const { limit, offset } = loadBoardBadgesFilters;

    let apiUrl = `${API_CONSTANTS.shipments}/odata?orderby=ShipmentStatus/SortOrder,deliveryStartDate asc`;

    if (isInclude) {
        const params = new Set([
            'ShipmentLocations',
            'ShipmentLocations.Location',
            'ShipmentLocations.Location.TagGroup',
            'Associate',
            'ShipmentOrders',
            'ShipmentOrders.Order',
            'ShipmentOrders.Order.Associate',
            'ShipmentStatus',
            'DriverLoads',
            'DriverLoads.Associate',
        ]);

        if (!isEnhancedLoadBoardEnabled) {
            [
                'DriverLoads.Associate.Contacts',
                'DriverLoads.Attachments',
                'ShipmentCargoDetails',
                'ShipmentCargoDetails.Commodity',
                'ShipmentCargoDetails.ShipmentCargoLocations',
                'DriverLoads.DriverLoadCargoDetails',
                'DriverLoads.DriverLoadCargoDetails.Commodity',
            ].forEach(i => params.add(i));
        }

        if (isTrailerSizePreferencesEnabled) {
            params.add(TRAILER_SIZE_INCLUDES[0]);
            params.add(TRAILER_SIZE_INCLUDES[1]);
        }

        apiUrl += `&include=${[...params].join(',')}`;
    }

    const statusFilters: string = buildStatusFilters(loadBoardBadgesFilters);
    let filterString =
        'IsActive eq true and ShipmentOrders/all(so: so/Order/Status ne 2)';

    const additionalFilters = buildFilterString({
        driverName:
            driverName === t('LB_Common:selectDriverName') ? '' : driverName,
        customerName:
            customerName === t('LB_Common:selectCustomerName')
                ? ''
                : customerName,
        orderDate,
        orderNumber,
        contractNumber,
    });

    if (additionalFilters) {
        filterString += ` and ${additionalFilters}`;
    }

    if (statusFilters) {
        filterString += ` and (${statusFilters})`;
    }

    apiUrl += `&$filter=${filterString}`;

    if (limit) {
        apiUrl += `&$top=${offset + limit}`;
    }

    return apiUrl;
};

const buildStatusFilters = (
    loadBoardBadgesFilters: ILoadBoardStatusFilters,
): string => {
    if (loadBoardBadgesFilters.selectedBadges.length === 0) {
        return '';
    }

    return `(${loadBoardBadgesFilters.selectedBadges
        .map(badge => `(ShipmentStatus/Name eq '${badge}')`)
        .join(' or ')})`;
};

const buildFilterString = (
    loadBoardSearchFilters: ILoadBoardSearchFilters,
): string => {
    let filterStrings: (string | undefined)[] = Object.entries(
        loadBoardSearchFilters,
    )
        .filter(
            ([_, value]) =>
                value !== '' && value !== null && value !== undefined,
        )
        .map(([key, value]) => {
            if (key === ApplyFiltersFormField.orderDate.name) {
                const filter = searchDateFilterDetailsMap.get(key);
                return filter?.getFilter(value as RangeValue);
            } else {
                const filter = searchFilterDetailsMap.get(key);
                return filter?.getFilter(value as string);
            }
        });

    if (isODataFilterFixEnabled) {
        filterStrings = filterStrings.filter(
            (filterString): filterString is string =>
                Boolean(filterString) && filterString?.trim() !== '',
        );
    }

    if (filterStrings.length <= 0) {
        return '';
    }

    return filterStrings.join(' and ');
};

export const updateShipmentService =
    (payload: IShipmentLoadBoardViewItem, shipmentId: number) =>
    async (dispatch: AppDispatch) => {
        try {
            await shipmentsApi.updateShipment(shipmentId, payload);
            notifySuccess(t('BS_CommonConstants:shipmentSaveMessage'));
        } finally {
            dispatch(setIsSubmitting(false));
        }
    };

export const updateShipmentStatus = async (
    shipmentId: number,
    selectedDriverIdList: ISelectedDriverIdList[],
    message: string,
): Promise<void> => {
    const response = await shipmentsApi.updateShipmentStatus(
        shipmentId,
        selectedDriverIdList,
    );
    if (!response.error) notifySuccess(i18n.t(message));
};
